import {
  transcriptorApi,
  type DocumentAnswers,
} from '@/shared/api/transcriptor';

import type { EncounterKind } from '@/entities/templates';

import { RECORDING_MODE_LIVE_MODE, type RecordingMode } from '../config';
import {
  createLiveDictation,
  openLiveTranscription,
  type AnswerEdit,
  type LiveDictation,
  type LiveDictationDeps,
  type LiveDictationState,
} from '../lib';
import { PcmCapture } from '../services';

export type LiveSessionMeta = {
  patientId: string;
  encounterId: string;
  kind: EncounterKind;
  // The name of the template the recording started from.
  title: string;
  canEdit: boolean;
};

export type LiveSession = {
  meta: LiveSessionMeta;
  // Null for a recording without live mode, e.g. a consultation.
  live: LiveDictationState | null;
  // Counts stored manual edits: a finished document re-renders after one.
  savedEdits: number;
};

export type LiveSessionStart = {
  stream: MediaStream | null;
  language: string;
  mode: RecordingMode;
};

const LIVE_DICTATION_DEPS: LiveDictationDeps = {
  api: transcriptorApi,
  createCapture: () => (PcmCapture.isSupported() ? new PcmCapture() : null),
  connect: (session, handlers) => openLiveTranscription({ session, handlers }),
};

// The recording's live session outlives the page that started it: the
// realtime transcription, the live fill and the answers edit queue stay here
// while the clinician moves between pages. One session at a time; a new
// recording replaces it.
export const createLiveSessionStore = (deps: LiveDictationDeps) => {
  const runtime = {
    session: null as LiveSession | null,
    controller: null as LiveDictation | null,
    listeners: [] as (() => void)[],
  };

  const publish = (session: LiveSession | null) => {
    runtime.session = session;
    runtime.listeners.forEach((listener) => listener());
  };

  const update = (
    controller: LiveDictation,
    recipe: (session: LiveSession) => LiveSession,
  ) => {
    if (runtime.controller === controller && runtime.session) {
      publish(recipe(runtime.session));
    }
  };

  return {
    getSnapshot: () => runtime.session,

    subscribe: (listener: () => void) => {
      runtime.listeners = [...runtime.listeners, listener];
      return () => {
        runtime.listeners = runtime.listeners.filter(
          (item) => item !== listener,
        );
      };
    },

    begin: (meta: LiveSessionMeta, live?: LiveSessionStart) => {
      runtime.controller?.dispose();
      runtime.controller = null;
      publish({ meta, live: null, savedEdits: 0 });
      if (!live) {
        return;
      }
      const controller: LiveDictation = createLiveDictation({
        encounterId: meta.encounterId,
        language: live.language,
        mode: RECORDING_MODE_LIVE_MODE[live.mode],
        deps,
        onChange: (state) =>
          update(controller, (session) => ({ ...session, live: state })),
        onSaved: () =>
          update(controller, (session) => ({
            ...session,
            savedEdits: session.savedEdits + 1,
          })),
      });
      runtime.controller = controller;
      void controller.start(live.stream);
    },

    clear: () => {
      runtime.controller?.dispose();
      runtime.controller = null;
      publish(null);
    },
    pause: () => runtime.controller?.pause(),
    resume: () => runtime.controller?.resume(),
    stop: async () => runtime.controller?.stop(),
    setDocuments: (documentIds: string[]) =>
      runtime.controller?.setDocuments(documentIds),
    edit: (documentId: string, edit: AnswerEdit) =>
      runtime.controller?.edit(documentId, edit),
    // Answers stored by another request, e.g. the values set before the
    // start; older revisions are ignored.
    receiveAnswers: (documentId: string, answers: DocumentAnswers) =>
      runtime.controller?.receiveAnswers(documentId, answers),
    loadFinalAnswers: async (documentId: string) =>
      runtime.controller?.loadFinalAnswers(documentId),
    readAnswers: async (documentId: string) =>
      runtime.controller?.readAnswers(documentId),
  };
};

export type LiveSessionStore = ReturnType<typeof createLiveSessionStore>;

export const liveSession = createLiveSessionStore(LIVE_DICTATION_DEPS);
